import { batch } from 'react-redux';
import { CAMERA_PRESENTER } from '@shoflo/polymer/constants/SourceItems';
import setDesiredAttendeeCameraStatus from 'actions/Broadcast/attendees/setDesiredAttendeeCameraStatus';
import setProgram from 'actions/Broadcast/program/setProgram';
import setSoloLive from 'actions/Broadcast/program/setSoloLive';
import updateSource from 'actions/Broadcast/sources/updateSource';
import {
  getSourceValue,
  isCameraPresenter,
  isScreenPresenter,
  isSourceInProgram,
  muteSourcesRemovedFromProgram
} from 'utils/broadcastSourceHelper';
import { getBroadcastSourcesFromLayers } from '../utils';
import setDesiredAttendeeMuteStatus from '@shoflo/studio-redux/actions/attendee/setDesiredAttendeeMuteStatus';
import toggleProgramSource from '@shoflo/studio-redux/actions/program/toggleProgramSource';
import { StudioLogger } from '@studio/logger/StudioLogger';
import {
  willExceedCameraLimit,
  hasMeetingExceededCameraLimit
} from '@shoflo/studio-redux/utils/sources';

const logger = new StudioLogger('program-update');

/**
 * Handles series of actions to be dispatched before taking a presenter source live (Add/Solo)
 */
function takePresenterLive(source, sourceActionConfig = {}) {
  return (dispatch, getState) => {
    const {
      attendeeCameraStatus,
      program: { layers = [] },
      broadcastSettings: { autoMutePresenters },
      broadcastSources,
      meeting: { meetingResponse, attendeeTiles }
    } = getState();

    const { isSoloLive } = sourceActionConfig;
    const { hidden: isSourceHidden, _id: sourceId } = source;
    const isLiveSource = isSourceInProgram(layers, sourceId);

    const sourceAction = isSoloLive ? setSoloLive : toggleProgramSource;
    const isScreenShare = isScreenPresenter(source);
    const sourceValue = getSourceValue(source);

    const addingSource = !layers.find(layer => layer.source._id === sourceId);
    const sources = getBroadcastSourcesFromLayers(
      layers,
      broadcastSources?.sources
    );

    // Stage limit (10) - controls mute behavior
    const isStageAtLimit = addingSource
      ? willExceedCameraLimit(sources, source)
      : false;

    // Camera ON limit (15 Agora / 10 Chime) - controls camera turn-on
    const isCameraOnAtLimit = addingSource
      ? hasMeetingExceededCameraLimit(attendeeTiles, attendeeCameraStatus)
      : false;

    const meetingId = meetingResponse?.Meeting?.MeetingId;

    const removedSources = isSoloLive
      ? sources?.filter(src => src._id !== sourceId)
      : !addingSource
      ? [source]
      : [];

    removedSources?.forEach(src => {
      if (isCameraPresenter(src)) {
        const logPayload = {
          'studio.actionName': isSoloLive
            ? 'Set Solo Live'
            : 'Toggle Program Source',
          'studio.attendeeTileStatus': 'OFF-STAGE',
          'studio.tileAttendeeId': src.value,
          title: `ProgramUpdate: Attendee ${src.value} in meeting ${meetingId} is removed from the stage`
        };

        logger.info(logPayload.title, logPayload);
      }
    });

    if (!isLiveSource && addingSource && !isStageAtLimit) {
      const logPayload = {
        'studio.actionName': isSoloLive
          ? 'Set Solo Live'
          : 'Toggle Program Source',
        'studio.attendeeTileStatus': 'ON-STAGE',
        'studio.tileAttendeeId': source.value,
        title: `ProgramUpdate: Attendee ${source.value} in meeting ${meetingId} is added to the stage`
      };

      logger.info(logPayload.title, logPayload);
    }

    const nextMuteStatus = isLiveSource;

    let isAttendeeCameraOn = attendeeCameraStatus[sourceValue];

    if (typeof isAttendeeCameraOn !== 'boolean') {
      isAttendeeCameraOn = true;
    }
    // should only result in one combined re-render, not multiple
    batch(() => {
      // We only want to unmute a source when they are going live. if they are live already no need to unmute
      if (!isLiveSource && autoMutePresenters && !isStageAtLimit) {
        dispatch(setDesiredAttendeeMuteStatus(sourceValue, nextMuteStatus));
      } else if (isLiveSource && !isSoloLive && autoMutePresenters) {
        // we are removing this source(s) from live so mute
        dispatch(setDesiredAttendeeMuteStatus(sourceValue, true));
      }

      if (
        !isCameraOnAtLimit &&
        !isAttendeeCameraOn &&
        !isScreenShare &&
        !isLiveSource &&
        !source.cameraUnavailable
      ) {
        dispatch(
          setDesiredAttendeeCameraStatus(sourceValue, !isAttendeeCameraOn)
        );
      }

      // Set solo live action already has an update source action
      if (isSourceHidden && !isSoloLive) {
        dispatch(updateSource(sourceId, { hidden: !isSourceHidden }));
      }

      // if we set a camera presenter solo, we want to mute the sources being removed from program.
      if (
        isSoloLive &&
        source.type === CAMERA_PRESENTER &&
        autoMutePresenters
      ) {
        muteSourcesRemovedFromProgram(layers, source, dispatch);
      }

      dispatch(sourceAction(source, setProgram));
    });
  };
}

export default takePresenterLive;
